/**
 * MemberSelector — search-and-select combobox to add a member by name.
 *
 * Searches user profiles via the buddy-group-ops/search-users endpoint
 * and lets the admin pick from matching results.
 */

import { useState, useEffect, useRef } from 'react';
import { Plus, Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { searchUserProfiles, type UserProfileSearchResult } from '../api/buddy-group-service';

interface MemberSelectorProps {
  onAdd: (userProfileId: string) => void;
  isLoading: boolean;
}

export function MemberSelector({ onAdd, isLoading }: MemberSelectorProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserProfileSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState<UserProfileSearchResult | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    // If user already selected a result and the query matches, don't re-search
    if (selected && query === selected.display_name) return;

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const data = await searchUserProfiles(query);
        setResults(data);
        setShowDropdown(data.length > 0);
      } catch {
        setResults([]);
        setShowDropdown(false);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, selected]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (user: UserProfileSearchResult) => {
    setSelected(user);
    setQuery(user.display_name);
    setShowDropdown(false);
  };

  const handleAdd = () => {
    if (!selected) return;
    onAdd(selected.id);
    setSelected(null);
    setQuery('');
    setResults([]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    // Clear selected if user modifies the text
    if (selected && val !== selected.display_name) {
      setSelected(null);
    }
  };

  return (
    <div ref={containerRef} className="space-y-1">
      <label className="text-sm font-medium text-foreground" htmlFor="add-member-search">
        Add Member
      </label>
      <div className="flex items-end gap-2">
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            {isSearching ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <Search className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
          <Input
            id="add-member-search"
            placeholder="Search by name or email..."
            value={query}
            onChange={handleInputChange}
            onFocus={() => {
              if (results.length > 0 && !selected) setShowDropdown(true);
            }}
            disabled={isLoading}
            className="pl-9"
            autoComplete="off"
          />
          {showDropdown && (
            <ul className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border bg-popover shadow-md">
              {results.map((user) => (
                <li key={user.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
                    onClick={() => handleSelect(user)}
                  >
                    <span className="font-medium">{user.display_name}</span>
                    {user.email && (
                      <span className="ml-2 text-xs text-muted-foreground">{user.email}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          disabled={!selected || isLoading}
          onClick={handleAdd}
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      {selected && (
        <p className="text-xs text-muted-foreground">
          Selected: {selected.display_name} ({selected.email})
        </p>
      )}
    </div>
  );
}
